import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarRail,
  SidebarFooter,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/sidebar";
import {
  FileText,
  ChevronsUpDown,
  LogOut,
  CreditCard,
  Building,
  ChevronRight,
  BarChart3,
  LayoutGrid,
} from "lucide-react";
import { Header } from "./header";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useAuth } from "@/hooks/use-auth";
import { useBrandSettings } from "@/hooks/use-brand-settings";
import { FEATURE, PERMISSION, REINSURANCE_DEPT } from "@/lib/permissions";

const CURRENT_PORTAL_KEY = "reinsurance";
const CURRENT_PORTAL_FALLBACK_NAME = "Reinsurance";

const mainNav = [
  // {
  //   title: "Claims",
  //   icon: FileText,
  //   href: "/claims",
  // },
  // {
  //   title: "Pre-Auths",
  //   icon: BadgeCheck,
  //   href: "/pre-auths",
  // },
  // {
  //   title: "Customer Support",
  //   icon: LifeBuoy,
  //   href: "/support",
  // },
  // {
  //   title: "Providers",
  //   icon: Building,
  //   href: "/providers",
  // },
  // {
  //   title: "Merchant Payments",
  //   icon: CreditCard,
  //   href: "/payments",
  // },
  // {
  //   title: "Product Configuration",
  //   icon: Settings,
  //   href: "/configuration",
  // },
  // {
  //   title: "Underwriting",
  //   icon: FileText,
  //   href: "/underwriting",
  // },
  {
    title: "Reinsurance",
    icon: CreditCard,
    href: "/re-insurance",
    isActive: true,
    submenu: [
      {
        title: "Treaty",
        href: "/re-insurance/treaty",
        icon: FileText,
        requires: {
          feature: FEATURE.TREATY,
          permission: PERMISSION.VIEW_TREATIES,
        },
      },
      {
        title: "Facultative",
        href: "/re-insurance/facultative",
        icon: FileText,
      },
      {
        title: "Brokers",
        href: "/re-insurance/brokers",
        icon: Building,
        requires: {
          feature: FEATURE.BROKER,
          permission: PERMISSION.VIEW_BROKERS,
        },
      },
      {
        title: "Reinsurers",
        href: "/re-insurance/reinsurers",
        icon: Building,
        requires: {
          feature: FEATURE.REINSURER,
          permission: PERMISSION.VIEW_REINSURERS,
        },
      },
      {
        title: "Compliance",
        href: "/re-insurance/compliance",
        icon: FileText,
      },
      // {
      //   title: "Policy",
      //   href: "/re-insurance/policy",
      //   icon: FileText,
      // },
      {
        title: "Reports",
        href: "https://sandbox-metabase.edeneasyclaim.com/collection/43-reinsurance-reports",
        icon: BarChart3,
        external: true,
      },
    ],
  },
];

export function ProtectedLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const { user: ssoUser, departments, hasPermission } = useAuth();
  const { brand } = useBrandSettings();

  const filteredMainNav = mainNav.map((item) => {
    if (!item.submenu) return item;
    const submenu = item.submenu.filter((sub) => {
      if (!sub.requires) return true;
      return hasPermission(
        REINSURANCE_DEPT,
        sub.requires.feature,
        sub.requires.permission
      );
    });
    return { ...item, submenu };
  });

  const currentPortalName =
    departments?.find((d) => d.key === CURRENT_PORTAL_KEY)?.name ??
    CURRENT_PORTAL_FALLBACK_NAME;

  const handleLogout = async () => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/auth/logout`, {
        method: "POST",
      });
    } catch (error) {
      console.error("Error during SSO logout:", error);
    }
    window.location.href = "https://app.sandbox.ginja.ai/login";
  };

  const getCasUser = () => ssoUser ?? null;

  const casUser = getCasUser();
  const user = {
    name:
      casUser?.name ||
      `${casUser?.first_name || ""} ${casUser?.last_name || ""}`.trim() ||
      "User",
    email: casUser?.email || "user@example.com",
    avatar: "/avatars/default.png",
    teamRole: casUser?.team_role || [],
  };

  return (
    <SidebarProvider>
      <div className="flex  w-full">
        <Sidebar collapsible="icon">
          <SidebarHeader className="border-b px-3 py-4 mb-3">
            <div
              className="flex items-center gap-3 px-2 py-1 cursor-pointer"
              onClick={() => navigate("/")}
            >
              <div className="flex items-center justify-center h-8 w-8 bg-muted shrink-0 overflow-hidden rounded-[10px]">
                {brand.logo_url ? (
                  <img
                    src={brand.logo_url}
                    alt={`${brand.brand_name} logo`}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <LayoutGrid className="h-4 w-4" />
                )}
              </div>
              <div className="grid flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden min-w-0">
                <span className="truncate font-semibold text-sm">
                  {brand.brand_name}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {currentPortalName}
                </span>
              </div>
            </div>
          </SidebarHeader>
          <SidebarContent>
            <div className="flex flex-col gap-4">
              <div>
                <SidebarMenu className="flex flex-col gap-1 text-[#373839]">
                  {filteredMainNav.map((item) => (
                    <Collapsible
                      key={item.href}
                      asChild
                      defaultOpen={item.isActive}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        {item.submenu ? (
                          <>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton
                                tooltip={item.title}
                                className="relative pt-1 ml-5 w-full overflow-visible"
                              >
                                <div className="flex items-center gap-2">
                                  <item.icon className="h-4 w-4" />
                                  <span className="group-data-[collapsible=icon]:hidden font-[400]">
                                    {item.title}
                                  </span>
                                </div>
                                <ChevronRight className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden z-10" />
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <SidebarMenuSub className="flex flex-col gap-1 text-[#373839]">
                                {item.submenu.map((subItem) => (
                                  <SidebarMenuSubItem key={subItem.href}>
                                    <SidebarMenuSubButton asChild>
                                      {subItem.external ? (
                                        <a
                                          href={subItem.href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`flex items-center gap-2 ${
                                            location.pathname === subItem.href
                                              ? "text-accent-foreground"
                                              : ""
                                          }`}
                                        >
                                          {subItem.icon && (
                                            <subItem.icon className="h-4 w-4" />
                                          )}
                                          <span>{subItem.title}</span>
                                        </a>
                                      ) : (
                                        <Link
                                          to={subItem.href}
                                          className={`flex items-center gap-2 ${
                                            location.pathname === subItem.href
                                              ? "text-accent-foreground"
                                              : ""
                                          }`}
                                        >
                                          {subItem.icon && (
                                            <subItem.icon className="h-4 w-4" />
                                          )}
                                          <span>{subItem.title}</span>
                                        </Link>
                                      )}
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                ))}
                              </SidebarMenuSub>
                            </CollapsibleContent>
                          </>
                        ) : (
                          <SidebarMenuButton
                            asChild
                            tooltip={item.title}
                            className="pt-1 ml-5"
                          >
                            <Link
                              to={item.href}
                              className={
                                location.pathname === item.href
                                  ? "bg-accent"
                                  : ""
                              }
                            >
                              <item.icon className="h-4 w-4" />
                              <span className="group-data-[collapsible=icon]:hidden font-[400]">
                                {item.title}
                              </span>
                            </Link>
                          </SidebarMenuButton>
                        )}
                      </SidebarMenuItem>
                    </Collapsible>
                  ))}
                </SidebarMenu>
              </div>
            </div>
          </SidebarContent>
          <SidebarFooter className="mb-2">
            <SidebarMenu>
              <SidebarMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                    >
                      <Avatar className="h-8 w-8 rounded-lg">
                        <AvatarImage src={user.avatar} alt={user.name} />
                        <AvatarFallback className="rounded-lg">
                          {user.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                        <span className="truncate font-semibold">
                          {user.name}
                        </span>
                        <span className="truncate text-xs">{user.email}</span>
                      </div>
                      <ChevronsUpDown className="ml-auto h-4 w-4 group-data-[collapsible=icon]:hidden" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-80 rounded-lg"
                    align="end"
                    side="right"
                    sideOffset={8}
                  >
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex items-center gap-2 p-2">
                        <Avatar className="h-8 w-8 rounded-lg">
                          <AvatarImage src={user.avatar} alt={user.name} />
                          <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="grid flex-1">
                          <span className="font-semibold break-words">
                            {user.name}
                          </span>
                          <span className="text-xs break-words">
                            {user.email}
                          </span>
                          {user.teamRole.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {user.teamRole.map(
                                (
                                  teamRole: { team: string; role: string },
                                  index: number,
                                ) => (
                                  <div key={index} className="break-words">
                                    {teamRole.team} - {teamRole.role}
                                  </div>
                                ),
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuItem onClick={handleLogout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>

        <main className="flex-1 overflow-y-auto transition-all duration-300 ease-in-out">
          <div className="p-2">
            <Header />
            <Outlet />
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
